import { Button, formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import {
  Activity,
  CreditCard,
  FileText,
  Search,
  Settings as SettingsIcon,
  Shield,
  UserCog,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { sqlUtcToIso } from '../../lib/sqlTimestamp.js';
import { SkeletonRows } from '../_ops/SkeletonRows.js';
import type { ActivityEventDto } from './contractsMirror.js';
import { activityEventLabel } from './eventLabels.js';
import type { ActivitySearch } from './searchParams.js';
import { useActivityQuery } from './useActivityQuery.js';

/**
 * The redesigned Activity screen (UI-SPEC archetype F — read-only audit feed): a timeline of
 * events, each row icon-coded by action type (actor + action + timestamp), with type-filter
 * pills (each a `<Link>` that sets `?type`), a date-range filter, and a description search.
 *
 * The feed is READ-ONLY — `useActivityQuery` is a plain `GET /api/v1/activity` that carries no
 * tenant id and NEVER mutates (Pitfall 3 / T-22-getmutate). The server resolves the tenant from
 * the principal (SEC-11). Every color is a locked token (BRAND-06).
 */

const INPUT =
  'h-11 w-full rounded-md border border-border bg-card px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** Map an event type's leading segment to an icon (token-styled, no hardcoded color). */
function eventIcon(eventType: string) {
  const head = eventType.split('.')[0] ?? '';
  switch (head) {
    case 'invoice':
    case 'estimate':
      return FileText;
    case 'payment':
      return CreditCard;
    case 'user':
      return UserCog;
    case 'settings':
      return SettingsIcon;
    case 'support':
      return Shield;
    default:
      return Activity;
  }
}

function localActivityTime(createdAt: string, locale: string): string {
  return formatUiDate(sqlUtcToIso(createdAt), locale, { dateStyle: 'short', timeStyle: 'short' });
}

function activityDescription(
  t: (key: string, options?: Record<string, string>) => string,
  event: ActivityEventDto,
): string {
  const actor = event.actorName ?? event.actorEmail ?? t('activity:actor.unknown');
  switch (event.eventType) {
    case 'auth.spa_login':
      return t('activity:descriptions.auth.spaLogin', { actor });
    case 'auth.mobile_login':
      return t('activity:descriptions.auth.mobileLogin', { actor });
    case 'auth.mobile_logout':
      return t('activity:descriptions.auth.mobileLogout', { actor });
    case 'auth.mobile_login_rotate':
      return t('activity:descriptions.auth.mobileSessionRefreshed', { actor });
    default:
      return event.description;
  }
}

function ActivityRow({ event }: { event: ActivityEventDto }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const Icon = eventIcon(event.eventType);
  const eventLabel = activityEventLabel(t, event.eventType);
  return (
    <li className="flex gap-3 rounded-md border border-border bg-card p-4">
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-foreground text-sm">{activityDescription(t, event)}</p>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-muted-foreground text-xs">
          <span className="font-medium">{event.actorName ?? event.actorEmail ?? '—'}</span>
          <span aria-hidden="true">·</span>
          <span>{eventLabel}</span>
          <span aria-hidden="true">·</span>
          <span className="font-mono">{localActivityTime(event.createdAt, locale)}</span>
        </p>
      </div>
    </li>
  );
}

function ActivityFilterSection({
  search,
  eventTypes,
  actors,
  queryText,
  setQueryText,
  onApply,
  onClear,
}: {
  search: ActivitySearch;
  eventTypes: string[];
  actors: Array<{ id: number; name: string; email: string }>;
  queryText: string;
  setQueryText: (value: string) => void;
  onApply: (next: ActivitySearch) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ActivitySearch>(search);

  useEffect(() => setDraft(search), [search]);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    onApply({ ...draft, q: queryText.trim() || undefined });
  }

  return (
    <form
      onSubmit={submit}
      aria-label={t('activity:filter.regionLabel')}
      className="mb-5 rounded-xl border border-border bg-card p-5 shadow-sm"
    >
      <div className="mb-4">
        <h2 className="font-display font-semibold text-foreground text-lg">
          {t('activity:filter.heading')}
        </h2>
        <p className="mt-1 text-muted-foreground text-sm">{t('activity:filter.body')}</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <label className="flex flex-col gap-1.5">
          <span className="font-medium text-foreground text-xs">
            {t('activity:filter.typeLabel')}
          </span>
          <select
            className={INPUT}
            value={draft.type ?? ''}
            onChange={(event) => setDraft({ ...draft, type: event.target.value || undefined })}
          >
            <option value="">{t('activity:filter.allTypes')}</option>
            {eventTypes.map((type) => (
              <option key={type} value={type}>
                {activityEventLabel(t, type)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-medium text-foreground text-xs">
            {t('activity:filter.actorLabel')}
          </span>
          <select
            className={INPUT}
            value={draft.actorUserId ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, actorUserId: event.target.value || undefined })
            }
          >
            <option value="">{t('activity:filter.allActors')}</option>
            {actors.map((actor) => (
              <option key={actor.id} value={actor.id}>
                {actor.name} · {actor.email}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-medium text-foreground text-xs">{t('activity:date.from')}</span>
          <input
            type="date"
            value={draft.from ?? ''}
            onChange={(event) => setDraft({ ...draft, from: event.target.value || undefined })}
            className={INPUT}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-medium text-foreground text-xs">{t('activity:date.to')}</span>
          <input
            type="date"
            value={draft.to ?? ''}
            onChange={(event) => setDraft({ ...draft, to: event.target.value || undefined })}
            className={INPUT}
          />
        </label>
        <label className="flex flex-col gap-1.5 md:col-span-2 xl:col-span-1">
          <span className="font-medium text-foreground text-xs">{t('activity:search.label')}</span>
          <span className="relative">
            <Search
              className="pointer-events-none absolute top-3.5 left-3 size-4 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="search"
              value={queryText}
              onChange={(event) => setQueryText(event.target.value)}
              placeholder={t('activity:search.placeholder')}
              className={`${INPUT} pr-3 pl-9`}
            />
          </span>
        </label>
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClear}>
          {t('activity:filter.clear')}
        </Button>
        <Button type="submit" variant="secondary">
          {t('activity:filter.apply')}
        </Button>
      </div>
    </form>
  );
}

const routeApi = getRouteApi('/activity');

export function ActivityRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = routeApi.useSearch();
  const query = useActivityQuery(search);

  const events = query.data?.events ?? [];
  const eventTypes = query.data?.eventTypes ?? [];
  const actors = query.data?.actors ?? [];

  // Controlled search/date inputs; the URL is the source of truth (deep-linkable).
  const [queryText, setQueryText] = useState(search.q ?? '');

  function applyFilters(next: ActivitySearch) {
    void navigate({ to: '/activity', search: next });
  }

  function clearFilters() {
    setQueryText('');
    void navigate({ to: '/activity', search: {} });
  }

  const hasEvents = events.length > 0;
  const headerSubtitle = useMemo(() => t('activity:subtitle'), [t]);

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {t('activity:title')}
        </h1>
        <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{headerSubtitle}</p>
      </header>

      <ActivityFilterSection
        search={search}
        eventTypes={eventTypes}
        actors={actors}
        queryText={queryText}
        setQueryText={setQueryText}
        onApply={applyFilters}
        onClear={clearFilters}
      />

      {query.isPending ? (
        <SkeletonRows rows={10} columns={3} label={t('activity:title')} />
      ) : query.isError ? (
        <section
          role="alert"
          aria-live="assertive"
          className="rounded-md border border-border bg-card p-8 text-center"
        >
          <p className="text-muted-foreground text-sm">{t('activity:error.body')}</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="mt-4 inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {t('activity:error.retry')}
          </button>
        </section>
      ) : hasEvents ? (
        <ul aria-label={t('activity:feed.label')} className="space-y-3">
          {events.map((event) => (
            <ActivityRow key={event.id} event={event} />
          ))}
        </ul>
      ) : (
        <p className="rounded-md border border-border bg-card p-8 text-center text-muted-foreground text-sm">
          {t('activity:empty')}
        </p>
      )}

      {query.data?.hasMore ? (
        <p className="mt-4 text-center text-muted-foreground text-xs">{t('activity:more')}</p>
      ) : null}
    </div>
  );
}
